import { useRef } from 'react';
import SEO from '@/components/SEO';
import { legal } from '@/data/content';
import { Download } from 'lucide-react';
import html2pdf from 'html2pdf.js';

export default function Privacy() {
  const contentRef = useRef<HTMLDivElement>(null);

  const downloadPDF = () => {
    if (!contentRef.current) return;

    const element = contentRef.current;
    // @ts-ignore
    html2pdf().set({
      margin: 10,
      filename: 'ZuriHub-Privacy-Policy.pdf',
      image: { type: 'jpeg', quality: 0.98 },
      html2canvas: { scale: 2, useCORS: true, letterRendering: true },
      jsPDF: { unit: 'mm', format: 'a4', orientation: 'portrait' },
    }).from(element).save();
  };

  return (
    <>
      <SEO
        title="Privacy Policy - ZuriHub Data Protection & Security"
        description="ZuriHub's comprehensive privacy policy covering data collection, usage, storage, and your rights. Learn how we protect your customer data and comply with GDPR, CCPA, and African data protection regulations."
        path="/privacy"
      />

      <section className="relative overflow-hidden bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900 px-4 py-24 text-white sm:px-6 lg:px-8">
        <div className="absolute inset-0 bg-[url('data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iNjAiIGhlaWdodD0iNjAiIHZpZXdCb3g9IjAgMCA2MCA2MCIgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIj48ZyBmaWxsPSJub25lIiBmaWxsLXJ1bGU9ImV2ZW5vZGQiPjxnIGZpbGw9IiNmZmYiIGZpbGwtb3BhY2l0eT0iMC4wNSI+PHBhdGggZD0iTTM2IDM0djMxYzAgMS42NTctMS4zNDMgMy0zIDNIMzN2LTMxYzAtMS42NTcgMS4zNDMtMyAzLTNoNnoiLz48L2c+PC9nPjwvc3ZnPg==')] opacity-20"></div>
        <div className="relative mx-auto max-w-4xl text-center">
          <div className="mb-6 inline-flex items-center justify-center rounded-full bg-blue-500/10 px-4 py-2 text-sm font-medium text-blue-400 ring-1 ring-blue-500/20">
            Legal Information
          </div>
          <h1 className="text-4xl font-extrabold tracking-tight sm:text-5xl lg:text-6xl bg-gradient-to-r from-white via-blue-100 to-slate-200 bg-clip-text text-transparent">
            {legal.privacy.title}
          </h1>
          <p className="mt-6 text-lg text-slate-300 max-w-2xl mx-auto">
            Comprehensive data protection policies and security measures to safeguard your information and ensure compliance with global regulations.
          </p>
          <div className="mt-8 flex flex-col items-center justify-center gap-4 sm:flex-row">
            <button
              onClick={downloadPDF}
              className="inline-flex items-center gap-2 rounded-lg bg-blue-600 px-6 py-3 text-white font-semibold hover:bg-blue-700 transition-all shadow-lg shadow-blue-500/25 hover:shadow-blue-500/40"
            >
              <Download className="h-5 w-5" />
              Download PDF
            </button>
            <div className="flex items-center gap-2 text-slate-400">
              <svg className="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
              </svg>
              <span className="text-sm">Last updated: {legal.privacy.lastUpdated}</span>
            </div>
          </div>
        </div>
      </section>

      <section className="py-16" aria-label="Privacy content">
        <div className="mx-auto max-w-3xl px-4 sm:px-6 lg:px-8">
          <div ref={contentRef} className="prose prose-lg max-w-none">
            <div className="mb-6 text-center">
              <h1 className="text-3xl font-bold text-slate-900">{legal.privacy.title}</h1>
              <p className="text-slate-600">Last updated: {legal.privacy.lastUpdated}</p>
            </div>

            {legal.privacy.sections.map((section) => (
              <div key={section.title} className="mb-8 page-break-inside-avoid">
                <h2 className="text-xl font-bold text-slate-900 mb-4">{section.title}</h2>
                {section.paragraphs.map((p, i) => (
                  <p key={i} className="mt-3 leading-relaxed text-slate-700 text-justify">
                    {p}
                  </p>
                ))}
              </div>
            ))}
          </div>
        </div>
      </section>
    </>
  );
}
